import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPhotos, resolveImage } from "@/lib/api";
import { X, ArrowUpRight } from "lucide-react";

/**
 * Standalone photography portfolio — intentionally NOT styled to match
 * "Vicky's Lost Girls". Bright, editorial, minimal.
 */
export default function Photography() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchPhotos()
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(photos.map((p) => p.category || "General"));
    return ["All", ...Array.from(set).sort()];
  }, [photos]);

  const shown = useMemo(
    () => (category === "All" ? photos : photos.filter((p) => (p.category || "General") === category)),
    [photos, category]
  );

  const year = new Date().getFullYear();

  return (
    <div
      data-testid="photography-page"
      className="photo-site min-h-screen relative"
      style={{ backgroundColor: "#f4efe6", color: "#1a1815" }}
    >
      {/* Top bar */}
      <header
        data-testid="photo-header"
        className="border-b border-[#1a1815]/10"
        style={{ backgroundColor: "#f4efe6" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div>
            <p className="font-dm text-[10px] tracking-[0.32em] uppercase text-[#8c6239]">
              Portfolio · {year}
            </p>
            <h1 className="font-fraunces text-2xl md:text-3xl font-light text-[#1a1815] leading-none mt-1">
              Vicky <span className="italic text-[#8c6239]">— Photography</span>
            </h1>
          </div>
          <nav className="flex items-center gap-6 md:gap-10 font-dm text-[11px] tracking-[0.24em] uppercase text-[#1a1815]/70">
            <a href="#work" className="hover:text-[#1a1815] transition-colors duration-300">
              Work
            </a>
            <a href="#about" className="hover:text-[#1a1815] transition-colors duration-300">
              About
            </a>
            <a href="#contact" className="hover:text-[#1a1815] transition-colors duration-300">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-14 md:pb-24">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-9">
            <p className="font-dm text-[10px] tracking-[0.32em] uppercase text-[#8c6239] mb-6">
              — Photography Journal · In Study
            </p>
            <h2
              data-testid="photo-hero-title"
              className="font-fraunces font-light text-[3.5rem] sm:text-[4.5rem] md:text-[6rem] lg:text-[7.5rem] leading-[0.9] text-[#1a1815] tracking-[-0.02em]"
            >
              A record of
              <br />
              <span className="italic text-[#8c6239]">looking closely.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-3">
            <p className="font-dm text-base md:text-[15px] text-[#1a1815]/70 leading-[1.75] border-t border-[#1a1815]/20 pt-5">
              A working portfolio kept while I study photography. New frames
              are added as the course unfolds — mistakes, moments, and quiet
              things that catch the light.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section id="work" className="max-w-[1400px] mx-auto px-6 md:px-12">
        {photos.some((p) => p.is_example) && (
          <div
            data-testid="photo-example-notice"
            className="mb-8 md:mb-10 border-l-2 border-[#8c6239] pl-4 py-1"
          >
            <p className="font-dm text-[10px] tracking-[0.28em] uppercase text-[#8c6239] mb-1">
              A note on this page
            </p>
            <p className="font-fraunces italic text-lg md:text-xl text-[#1a1815]/80 leading-snug">
              The frames marked <span className="not-italic font-dm text-[10px] tracking-[0.24em] uppercase bg-[#1a1815]/85 text-[#f4efe6] px-2 py-0.5 mx-0.5">Example Photo</span> are placeholders while my own coursework is added.
            </p>
          </div>
        )}
        <div className="flex items-baseline justify-between border-b border-[#1a1815]/15 pb-4 mb-10 md:mb-14 flex-wrap gap-4">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`photo-filter-${cat}`}
                className={`font-dm text-[11px] tracking-[0.2em] uppercase px-4 py-2 rounded-full border transition-all duration-300 ${
                  category === cat
                    ? "border-[#1a1815] bg-[#1a1815] text-[#f4efe6]"
                    : "border-[#1a1815]/25 text-[#1a1815]/70 hover:border-[#1a1815] hover:text-[#1a1815]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <span
            data-testid="photo-count"
            className="font-dm text-[11px] tracking-[0.24em] uppercase text-[#1a1815]/60"
          >
            {String(shown.length).padStart(2, "0")} {shown.length === 1 ? "Frame" : "Frames"}
          </span>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        {loading ? (
          <div
            data-testid="photo-loading"
            className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8"
          >
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="aspect-[4/5] bg-[#1a1815]/5 animate-pulse"
              />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div
            data-testid="photo-empty"
            className="text-center py-24 border-y border-[#1a1815]/10"
          >
            <p className="font-fraunces italic text-3xl md:text-4xl text-[#1a1815] mb-3">
              The first frame is on its way.
            </p>
            <p className="font-dm text-sm text-[#1a1815]/60">
              This page will fill as the coursework progresses.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {shown.map((photo, idx) => (
              <figure
                key={photo.id}
                className={`group ${idx % 5 === 0 ? "md:col-span-2 md:row-span-1" : ""}`}
              >
                <button
                  onClick={() => setLightbox(photo)}
                  data-testid={`photo-tile-${photo.id}`}
                  className="block w-full overflow-hidden bg-[#1a1815]/5 focus:outline-none focus:ring-2 focus:ring-[#8c6239] focus:ring-offset-4 focus:ring-offset-[#f4efe6] relative"
                >
                  <div className={`overflow-hidden ${idx % 5 === 0 ? "aspect-[16/10]" : "aspect-[4/5]"}`}>
                    <img
                      src={resolveImage(photo.image_url)}
                      alt={photo.title || "Photograph"}
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                  {photo.is_example && (
                    <span
                      data-testid={`photo-example-badge-${photo.id}`}
                      className="absolute top-3 left-3 font-dm text-[9px] tracking-[0.24em] uppercase bg-[#1a1815]/85 text-[#f4efe6] px-2.5 py-1 backdrop-blur-sm"
                    >
                      Example Photo
                    </span>
                  )}
                </button>
                <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                  <div>
                    {photo.title ? (
                      <p className="font-fraunces text-xl italic text-[#1a1815] leading-tight">
                        {photo.title}
                      </p>
                    ) : (
                      <p className="font-fraunces italic text-lg text-[#1a1815]/40">
                        Untitled
                      </p>
                    )}
                    {photo.caption && (
                      <p className="font-dm text-[13px] text-[#1a1815]/60 mt-1 leading-snug line-clamp-2">
                        {photo.caption}
                      </p>
                    )}
                  </div>
                  <span className="font-dm text-[10px] tracking-[0.24em] uppercase text-[#8c6239] whitespace-nowrap">
                    {photo.category}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* About & Contact */}
      <section
        id="about"
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 border-t border-[#1a1815]/10"
      >
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-dm text-[10px] tracking-[0.32em] uppercase text-[#8c6239] mb-3">
              About
            </p>
            <h3 className="font-fraunces font-light text-3xl md:text-4xl text-[#1a1815] leading-tight">
              Learning to see, one frame at a time.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="font-dm text-base md:text-lg text-[#1a1815]/75 leading-[1.85] max-w-2xl">
              I'm Vicky — currently studying photography and using this
              journal to keep the frames I'm proud of alongside the ones I'm
              still working out. Everything here is unedited coursework and
              personal practice. If a picture catches your eye, or you'd like
              to talk about a shoot, please get in touch.
            </p>
            <a
              id="contact"
              href="mailto:hello@vickys-lost-girls.co.uk"
              data-testid="photo-contact-link"
              className="inline-flex items-center gap-2 mt-8 font-dm text-sm text-[#1a1815] border-b border-[#1a1815] pb-1 hover:text-[#8c6239] hover:border-[#8c6239] transition-colors duration-300"
            >
              hello@vickys-lost-girls.co.uk
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer with discreet return link */}
      <footer className="border-t border-[#1a1815]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 font-dm text-[11px] tracking-[0.24em] uppercase text-[#1a1815]/50">
          <span>© {year} Vicky · Photography</span>
          <Link
            to="/"
            data-testid="photo-return-link"
            className="hover:text-[#1a1815] transition-colors duration-300"
          >
            ← A separate project
          </Link>
        </div>
      </footer>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="photo-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-[#1a1815]/95 flex items-center justify-center p-6 md:p-12 cursor-zoom-out"
        >
          <button
            data-testid="photo-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            className="absolute top-6 right-6 text-[#f4efe6] hover:text-[#8c6239] transition-colors duration-300"
          >
            <X className="w-6 h-6" strokeWidth={1.25} />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightbox.is_example && (
              <p
                data-testid="photo-lightbox-example-note"
                className="text-center font-dm text-[10px] tracking-[0.28em] uppercase text-[#8c6239] mb-4"
              >
                Example Photo · Placeholder
              </p>
            )}
            <img
              src={resolveImage(lightbox.image_url)}
              alt={lightbox.title || "Photograph"}
              className="w-full max-h-[80vh] object-contain"
            />
            {(lightbox.title || lightbox.caption) && (
              <div className="mt-6 text-center">
                {lightbox.title && (
                  <p className="font-fraunces italic text-2xl md:text-3xl text-[#f4efe6]">
                    {lightbox.title}
                  </p>
                )}
                {lightbox.caption && (
                  <p className="font-dm text-base text-[#f4efe6]/70 mt-2 max-w-2xl mx-auto">
                    {lightbox.caption}
                  </p>
                )}
                <p className="font-dm text-[10px] tracking-[0.28em] uppercase text-[#8c6239] mt-4">
                  {lightbox.category}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
